// Copyright 2025, Imprivata, Inc.  All rights reserved.

import {
  AuthnMethod,
  AuthnViews,
  type EventPayload,
  EventType,
} from '@imprivata-cloud/authn';

import UserHex from '@/assets/face-user.svg?react';
import { LayoutHeaders } from '@/containers/Card/authn-layout';
import {
  FACTOR_GLYPHS,
  gateGlyphOf,
  isAuthnMethod,
  SCREEN_GLYPHS,
} from '@/factors/factor-glyphs';

// FactorPhase
//
// The transient capture lifecycle that lives *inside* a screen: a factor starts
// authenticating, then finishes. Encoded as a sum so the central glyph is a pure
// projection of (screen, phase) rather than a cell poked from several events.
export type FactorPhase =
  | { kind: 'idle' }
  | { kind: 'authenticating'; method: AuthnMethod }
  | { kind: 'finished'; method: AuthnMethod };

// ViewState
//
// The unified presentation state for the Authn sub-machine. Every visible
// configuration (header, hex glyph, layout flags) is a total function of this
// value, so the event handler only ever transitions it — it never sets pixels.
export interface ViewState {
  screen?: AuthnViews;
  phase: FactorPhase;
  // start_enrolling: true while the enroll gate is mounted (intro through
  // success), so the (compact) enrollment hex header shows across the whole
  // gate; undefined once the gate unmounts (defer to the authentication header).
  enroll?: boolean;
  // start_reconsent: true while the face reconsent gate is mounted (intro +
  // consent), so the face-scan hex shows across the whole gate; undefined once
  // it unmounts. Reconsent emits no VIEW_CHANGED, so it is tracked explicitly.
  reconsent?: boolean;
}

export const INITIAL_VIEW: ViewState = { phase: { kind: 'idle' } };

// reduceView: the single writer of presentation state. Each view-config event is
// a transition; the screen-changing event also resets the in-screen capture phase.
export function reduceView(
  state: ViewState,
  { event, data }: EventPayload,
): ViewState {
  switch (event) {
    case EventType.VIEW_CHANGED:
      return { ...state, screen: data, phase: { kind: 'idle' } };
    case EventType.START_AUTHENTICATING:
      return isAuthnMethod(data)
        ? { ...state, phase: { kind: 'authenticating', method: data } }
        : state;
    case EventType.FINISH_AUTHENTICATING:
      return isAuthnMethod(data)
        ? { ...state, phase: { kind: 'finished', method: data } }
        : state;
    case EventType.START_ENROLLING:
      // Gate mount → true (enrollment header for its lifetime); unmount →
      // undefined (defer to the authentication header, no stale hex bar).
      return { ...state, enroll: data ? true : undefined };
    case EventType.START_RECONSENT:
      // Reconsent gate mount → true (face-scan hex for its lifetime); unmount →
      // undefined. Reconsent screens emit no VIEW_CHANGED, so this is the only
      // signal that the gate is on screen.
      return { ...state, reconsent: data ? true : undefined };
    // The flow is back at identification, so the factor screen's glyph and
    // header no longer apply.
    case EventType.USERNAME_CHANGE_REQUESTED:
      return INITIAL_VIEW;
    default:
      return state;
  }
}

// headerOf: the password screen and enroll sub-machine override the base mode;
// undefined defers to the authentication header in the consumer. Every header is
// rendered in the compact form (see authentication-header.less), so no screen is
// headerless — the consent documents fit under the shortened bar.
export function headerOf(view: ViewState): LayoutHeaders | undefined {
  if (view.screen === AuthnViews.PASSWORD_FORM) {
    return LayoutHeaders.AUTHENTICATION;
  }
  if (view.enroll) {
    // The enroll gate shows the enrollment hex header across its whole
    // lifetime — intro, consent, capture, and success.
    return LayoutHeaders.ENROLL_CONSENT;
  }
  return undefined;
}

// hexOf: while a capture is in flight the phase owns the central glyph; once
// idle the glyph is whatever the current screen canonically shows.
export function hexOf(view: ViewState): React.ReactNode | undefined {
  const { phase, screen, enroll, reconsent } = view;
  if (phase.kind === 'authenticating') {
    return FACTOR_GLYPHS[phase.method];
  }
  if (phase.kind === 'finished') {
    return phase.method === AuthnMethod.PASSWORD ||
      phase.method === AuthnMethod.TEMP_CODE ? (
      <UserHex />
    ) : undefined;
  }

  // A gate's consent and loader steps report no screen, so they fall back to the
  // face glyph their flow implies; every named screen owns its own.
  if (enroll || reconsent) {
    return gateGlyphOf(screen);
  }

  return screen ? SCREEN_GLYPHS[screen] : undefined;
}

export const showsInformationDialog = (view: ViewState): boolean =>
  view.screen === AuthnViews.INFORMATION_DIALOG;
